import React, { useCallback, useEffect, useState } from "react";
import { Breadcrumbs, Button, SimpleCheckboxControlled, TextInputBase } from "@truevault/aquilo";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../root/routes";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import {
  useOrganizationVendors,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
} from "../../../hooks";
import { OrganizationPublicId, UUIDString } from "../../../../common/service/server/Types";
import { useHistory, useParams } from "react-router";
import { useCollectionGroup } from "../../../hooks/useCollectionGroups";
import {
  DisplayPicGroup,
  OrderingVariant,
  useFilteredPicGroups,
} from "../../../components/organisms/collection-groups/ConsumerDataCollectionCheckboxes";
import { useDataMapCollectedPICIds } from "../../../../common/service/resources/data-map/dataMapTransformers";
import { useDataMapPendingCollectedPicIds } from "../../../../common/service/resources/data-map/dataMapPending";
import {
  BUSINESS_SURVEY_NAME,
  COLLECTION_FOR_OTHER_BUSINESSES_SLUG,
} from "../../../surveys/businessSurvey";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { PageLoading } from "../../../components";
import { CollectionCheckboxes } from "../../../components/get-compliant/collection-groups/CollectionCheckboxGroup";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { GroupIcon } from "../../get-compliant/data-map/GroupIcon";
import clsx from "clsx";
import { RefreshFn } from "../../../../common/hooks/api";
import { ArrowLeftIcon, FingerPrintIcon } from "@heroicons/react/24/outline";
import { useReceivedForCollectionGroups } from "../../../hooks/useReceivedForCollectionGroups";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { Api } from "../../../../common/service/Api";
import { CollectionExplanatoryCard } from "./DataFlowsExplanatoryCards";
import { useDataFlowsOnboardingFooterGate } from "./dataFlowsCompletion";
import { useDataFlowsDeepLinkFocus } from "./dataFlowsDeepLink";
import { useInvalidateOnboardingProgress } from "../../../../common/hooks/useOnboardingProgress";
import { KNOWN_EMPLOYMENT_VENDOR_CATEGORIES } from "../../../components/organisms/vendor/VendorCategories";
import {
  CATEGORY_RECOMMENDATION_THRESHOLD,
  categoryAdoption,
} from "./recommendations/categoryPopularity";
import { LightbulbIcon } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { CollectionContext } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";

export type DataFlowsCollectionPageConfig = {
  collectionContext: CollectionContext[];
  orderingVariant: OrderingVariant;
  dashboardRoute: string;
  breadcrumbSource: string;
  breadcrumbSourceRoute: string;
  headerElement?: React.ReactNode;
};

const CONSUMER_DATA_FLOWS_COLLECTION_CONFIG: DataFlowsCollectionPageConfig = {
  collectionContext: ["CONSUMER"],
  orderingVariant: "default",
  dashboardRoute: Routes.dataMap.dataFlows.dashboard,
  breadcrumbSource: "Data Flows",
  breadcrumbSourceRoute: Routes.dataMap.dataFlows.dashboard,
};

function PICGroupCheckboxPanel({
  htmlId,
  orgId,
  collectionGroupId,
  picGroup,
  collectedIds,
  pendingIds,
  onChecked,
  onBulkChecked,
  autoselected,
  dataMap,
  recommendedPICIds,
  bulkSelectablePICIds,
  recommendedPICVendorNames,
  focusedCategoryId,
  refreshDataMap,
}: {
  htmlId?: string;
  orgId: OrganizationPublicId;
  collectionGroupId: string;
  picGroup: DisplayPicGroup;
  dataMap: DataMapDto;
  refreshDataMap: RefreshFn;
  collectedIds: string[];
  pendingIds: string[];
  onChecked: (id: string, isCollected: boolean) => void;
  onBulkChecked: (ids: string[], isCollected: boolean) => void;
  autoselected?: string[];
  recommendedPICIds?: string[];
  bulkSelectablePICIds?: string[];
  recommendedPICVendorNames?: Record<string, string[]>;
  focusedCategoryId?: string | null;
}) {
  const hasSensitivePics = picGroup.categories.some((cat) => cat.sensitiveRegions?.length > 0);
  // Only non-sensitive recommendations can be bulk-selected — see the guard in the page body.
  const groupRecommendedIds = picGroup.categories
    .filter((cat) => bulkSelectablePICIds.includes(cat.id))
    .map((cat) => cat.id);
  const allRecommendedSelected =
    groupRecommendedIds.length > 0 && groupRecommendedIds.every((id) => collectedIds.includes(id));

  const isCustomGroup = picGroup.key === "custom";

  const [customPicName, setCustomPicName] = useState("");
  const [customPicLoading, setCustomPicLoading] = useState(false);

  const trimmedName = customPicName.trim();
  const isDuplicateName =
    trimmedName.length > 0 && Object.values(dataMap.categories).some((c) => c.name === trimmedName);

  const handleAddCustomPic = async () => {
    if (!trimmedName || isDuplicateName || customPicLoading) return;
    setCustomPicLoading(true);
    const pic = await Api.organization.createCustomPic(orgId, trimmedName);
    onChecked(pic.id, true);
    await refreshDataMap();
    setCustomPicLoading(false);
    setCustomPicName("");
  };

  const customPicAddForm = (
    <div className="mt-[12px]">
      <div className="flex items-center gap-[8px]">
        <TextInputBase
          field="customPicName"
          value={customPicName}
          onChange={(e) => setCustomPicName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddCustomPic();
            }
          }}
          placeholder="Add a category"
          disabled={customPicLoading}
          inputWidth="w-[250px]"
        />
        <Button
          color="primary"
          size="s"
          onClick={handleAddCustomPic}
          disabled={!trimmedName || isDuplicateName || customPicLoading}
        >
          Save
        </Button>
      </div>
      {isDuplicateName && (
        <p className="text-red-500 text-[12px] mt-[4px]">
          A category already exists with this name.
        </p>
      )}
    </div>
  );

  return (
    <div
      className={clsx(
        "w-[800px] p-[24px]",
        "rounded-[10px] border border-solid border-neutral-300",
      )}
      id={htmlId}
    >
      <div className="flex items-center justify-between gap-[12px]">
        {/* The title owns the flexible space and wraps within it, so the controls on the right
            always hold one line no matter how long the group name runs (crit c_e225b7). */}
        <div className="flex items-center min-w-0 flex-1">
          <GroupIcon groupName={picGroup.key} color="primary" />
          <div className="ml-[12px] font-medium">{picGroup.name}</div>
        </div>
        <div className="flex items-center gap-[12px] flex-shrink-0">
          {groupRecommendedIds.length > 0 && (
            /* One-click accept for this section's recommended categories — writes through the
               same PATCH as manual checking; unchecking removes exactly the recommended set. */
            <div
              className="flex items-center"
              style={{ backgroundColor: "#F3F0FE", borderRadius: 6, padding: "4px 12px" }}
            >
              <SimpleCheckboxControlled
                containerClassName="items-center gap-[6px]"
                name={`select-recommended-${picGroup.id}`}
                checked={allRecommendedSelected}
                onChange={(newChecked) => {
                  const ids = groupRecommendedIds.filter((id) =>
                    newChecked ? !collectedIds.includes(id) : collectedIds.includes(id),
                  );
                  if (ids.length > 0) {
                    onBulkChecked(ids, newChecked);
                  }
                }}
                label={
                  <span className="flex items-center gap-[4px] text-[13px] font-medium text-primary-800 whitespace-nowrap">
                    Check all recommended
                    <span className="text-primary-600">{LightbulbIcon}</span>
                  </span>
                }
              />
            </div>
          )}
          {hasSensitivePics && (
            <div className="flex items-center gap-[4px]">
              <div className="text-[14px] font-medium text-red-600 whitespace-nowrap">
                Sensitive Data
              </div>
              <FingerPrintIcon className="w-[16px] h-[16px] text-red-600" />
            </div>
          )}
        </div>
      </div>
      {picGroup.helptext?.length > 0 && (
        <div
          className="text-[14px] text-neutral-500 ml-[39px] max-w-[55ch]"
          dangerouslySetInnerHTML={{ __html: picGroup.helptext }}
        />
      )}
      <div className="pl-[36px] mt-[12px]">
        <CollectionCheckboxes
          refreshDataMap={refreshDataMap}
          orgId={orgId}
          categories={picGroup.categories}
          dataMap={dataMap}
          checked={collectedIds}
          recommendedPICIds={recommendedPICIds}
          recommendedPICVendorNames={recommendedPICVendorNames}
          focusedCategoryId={focusedCategoryId}
          onChecked={onChecked}
          disabledCategoryIds={[]}
          pendingIds={pendingIds}
          columns={true}
          allowCustom={false}
          autoselected={autoselected}
          collectionGroupId={collectionGroupId}
          useAquiloDesign={true}
          footerElement={<div>{isCustomGroup && customPicAddForm}</div>}
        />
      </div>
    </div>
  );
}

export function DataFlowsCollectionPage() {
  return <DataFlowsCollectionPageContent config={CONSUMER_DATA_FLOWS_COLLECTION_CONFIG} />;
}

export function DataFlowsCollectionPageContent({
  config,
}: {
  config: DataFlowsCollectionPageConfig;
}) {
  const history = useHistory();

  const [org] = usePrimaryOrganization();
  const orgId = usePrimaryOrganizationId();
  const {
    data: dataMap,
    mutators: { patch },
    refresh: refreshDataMap,
  } = useDataMap(orgId);

  const { groupId } = useParams<{ groupId: UUIDString }>();

  const [group] = useCollectionGroup(orgId, groupId);

  const { answers: bizAnswers } = useSurvey(orgId, BUSINESS_SURVEY_NAME);

  const collected = useDataMapCollectedPICIds(dataMap, { collectionGroupId: groupId });
  const pending = useDataMapPendingCollectedPicIds(dataMap, {
    collectionGroupId: groupId,
  });
  const filteredPicGroups = useFilteredPicGroups({
    dataMap,
    org,
    orderingVariant: config.orderingVariant,
  });

  const receivedData = useReceivedForCollectionGroups(orgId, [group], config.collectionContext);

  const [allVendors] = useOrganizationVendors(
    org?.id,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ config.collectionContext,
    /* onlyUseSettled = */ true,
  );

  const handleChecked = useCallback(
    (id: UUIDString, isCollected: boolean) => {
      patch.mutate({
        operations: [
          {
            action: isCollected ? "ADD" : "REMOVE",
            target: "COLLECTION",
            picId: id,
            groupId: group.id,
          },
        ],
      });
    },
    [group, patch],
  );

  const menuItem = (key: string, contents: string, isSelected: boolean, onClick: () => void) => {
    return (
      <div
        key={key}
        className={`w-[256px] py-[9px] px-[12px] text-[12px] rounded-[8px] font-medium cursor-pointer ${
          isSelected ? "text-neutral-900 bg-neutral-100" : "text-neutral-600"
        }`}
        style={{ marginRight: "30px" }}
        onClick={() => onClick && onClick()}
      >
        {contents}
      </div>
    );
  };

  // While the page is still loading, report "complete" so the footer doesn't flash in and out;
  // once loaded, an empty step keeps the footer even for orgs past onboarding.
  const isPageDataLoaded = Boolean(group && bizAnswers && dataMap && receivedData && allVendors);
  const showOnboardingFooter = useDataFlowsOnboardingFooterGate(
    config.collectionContext.includes("EMPLOYEE"),
    !isPageDataLoaded || collected.length > 0,
  );
  const invalidateOnboardingProgress = useInvalidateOnboardingProgress(orgId);
  // Arriving from the finished data-map diagram: scroll to the category that was clicked.
  const focusedCategoryId = useDataFlowsDeepLinkFocus(
    "category",
    "category",
    Boolean(dataMap) && filteredPicGroups.length > 0,
  );

  // Which section the rail should mark as current. Driven by an observer so it follows scrolling,
  // not just clicks; the topmost intersecting panel wins.
  const [activePicGroupKey, setActivePicGroupKey] = useState<string | null>(null);
  // Derived during render, not stashed in a ref: a ref assigned further down the body is still
  // empty when the effect's deps are compared on the first loaded render, so the observer would
  // never attach unless something else happened to force another render.
  const panelKeysSignature = filteredPicGroups.map((picGroup) => picGroup.key).join(",");
  useEffect(() => {
    const keys = panelKeysSignature.length > 0 ? panelKeysSignature.split(",") : [];
    // The signature can be settled before the panels exist: arriving from the dashboard, dataMap is
    // already cached while allVendors is still loading and the page is showing <PageLoading />.
    // Wait for the loaded render so the observer has elements to attach to.
    if (!isPageDataLoaded || keys.length === 0) {
      return;
    }
    const elements = keys
      .map((key) => document.getElementById(`pic-group-${key}`))
      .filter((el): el is HTMLElement => el != null);
    if (elements.length === 0) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActivePicGroupKey(visible[0].target.id.replace("pic-group-", ""));
        }
      },
      // Bias towards the upper part of the viewport so the marked section is the one being read.
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [panelKeysSignature, isPageDataLoaded]);

  if (!group || !bizAnswers || !dataMap || !receivedData || !allVendors) {
    return <PageLoading />;
  }

  const isHr = config.collectionContext.includes("EMPLOYEE");

  // Vendor evidence is curated per vendor, not per audience. On the consumer page an HR vendor
  // (ADP, Ceridian, Checkr, …) would otherwise recommend SSN / passport / disability "because
  // you use ADP" — in the sanitized dataset 0% of orgs using those vendors collect those
  // categories about consumers. So consumer recommendations skip employment-category vendors.
  const employmentVendorCategories = new Set(
    KNOWN_EMPLOYMENT_VENDOR_CATEGORIES.map((c) => c.category),
  );
  const evidenceVendors = isHr
    ? allVendors
    : allVendors.filter((v) => !employmentVendorCategories.has(v.category));
  const vendorRecommendedPICIds = [
    ...new Set(evidenceVendors.flatMap((v) => v.recommendedPersonalInformationCategories)),
  ];

  const recommendedPICVendorNames = evidenceVendors.reduce<Record<string, string[]>>(
    (acc, vendor) => {
      for (const picId of vendor.recommendedPersonalInformationCategories) {
        (acc[picId] ??= []).push(vendor.name);
      }
      return acc;
    },
    {},
  );

  // Hard guard: a sensitive category is never recommended and never bulk-selectable, whatever
  // the source. Claiming to collect sensitive data is a legal assertion, so it always has to be
  // an individual, deliberate click — vendor recommendations (admin-curated, and they do include
  // sensitive categories for vendors like Twilio or UKG) are subject to this too.
  const isSensitiveCategory = (cat: typeof dataMap.categories[string]) =>
    cat.isSensitive ||
    (cat.sensitiveRegions?.length ?? 0) > 0 ||
    cat.surveyClassification === "SENSITIVE" ||
    cat.surveyClassification === "GDPR_SENSITIVE";

  const isSensitivePicId = (picId: string) => {
    const cat = dataMap.categories[picId];
    // Unknown ids are treated as sensitive: better to leave a category out of a bulk action
    // than to assert collection we can't classify.
    return cat == null || isSensitiveCategory(cat);
  };

  // Cross-org popularity recommendations (consumer only — the snapshot is consumer-derived).
  const popularityRecommendedPICIds: string[] = [];
  if (!isHr) {
    for (const cat of Object.values(dataMap.categories)) {
      const adoption = categoryAdoption(cat.key);
      if (adoption == null || isSensitiveCategory(cat)) {
        continue;
      }
      if (adoption >= CATEGORY_RECOMMENDATION_THRESHOLD) {
        popularityRecommendedPICIds.push(cat.id);
      }
    }
  }

  // Sensitive vendor recommendations keep their lightbulb (the vendor really does receive that
  // category), but they are excluded from the bulk-select set below.
  const recommendedPICIds = [
    ...new Set([...vendorRecommendedPICIds, ...popularityRecommendedPICIds]),
  ];
  const bulkSelectablePICIds = recommendedPICIds.filter((picId) => !isSensitivePicId(picId));

  // Bulk accept/clear for a section's recommended categories: one PATCH, N operations —
  // the same write path as manual checking, so undo is just unchecking.
  const handleBulkChecked = (ids: string[], isCollected: boolean) => {
    // Second line of defense: even if a caller passes a sensitive id, an ADD never writes it.
    const safeIds = isCollected ? ids.filter((picId) => !isSensitivePicId(picId)) : ids;
    if (safeIds.length === 0) {
      return;
    }
    patch.mutate({
      operations: safeIds.map((picId) => ({
        action: isCollected ? ("ADD" as const) : ("REMOVE" as const),
        target: "COLLECTION" as const,
        picId,
        groupId: group.id,
      })),
    });
  };

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        showOnboardingFooter && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Data Collection"
                nextOnClick={() => {
                  invalidateOnboardingProgress();
                  history.push(config.dashboardRoute);
                }}
                nextDisabled={collected && collected.length == 0}
                nextDisabledTooltip="Select at least one data category"
              />
            }
          />
        )
      }
    >
      <div className="tw-boundary">
        <div className="mb-[32px]">
          <Breadcrumbs
            hideHomeIcon={true}
            title={`Data Collection - ${group.name}`}
            source={config.breadcrumbSource}
            sourceRoute={config.breadcrumbSourceRoute}
          />
        </div>
        {config.headerElement}
        <div className="flex">
          <div className="sticky top-[24px] self-start">
            <div
              className="text-neutral-900 font-medium text-[14px] flex items-center cursor-pointer gap-[12px] ml-[12px] mb-[12px]"
              onClick={() => {
                history.push(config.dashboardRoute);
              }}
            >
              <ArrowLeftIcon className="w-[20px] h-[20px]" />
              Back to Data Flows
            </div>
            {filteredPicGroups.map((picGroup) =>
              menuItem(
                picGroup.key,
                picGroup.name,
                picGroup.key === (activePicGroupKey ?? filteredPicGroups[0]?.key),
                () => {
                  const element = document.getElementById(`pic-group-${picGroup.key}`);
                  if (element) {
                    setActivePicGroupKey(picGroup.key);
                    element.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                },
              ),
            )}
          </div>
          <div>
            <div className="max-w-[800px]">
              <CollectionExplanatoryCard
                collectionGroupName={group.name}
                isHr={config.collectionContext.includes("EMPLOYEE")}
                showCollectionForOtherBusinesses={
                  !config.collectionContext.includes("EMPLOYEE") &&
                  bizAnswers[COLLECTION_FOR_OTHER_BUSINESSES_SLUG] === "true"
                }
                showRecommendationGuidance={recommendedPICIds.length > 0}
              />
            </div>
            <div className="space-y-3" style={{ marginTop: 24 }}>
              {filteredPicGroups.map((picGroup) => {
                return (
                  <PICGroupCheckboxPanel
                    key={picGroup.id}
                    htmlId={`pic-group-${picGroup.key}`}
                    orgId={orgId}
                    refreshDataMap={refreshDataMap}
                    collectionGroupId={groupId}
                    picGroup={picGroup}
                    dataMap={dataMap}
                    collectedIds={collected}
                    pendingIds={pending}
                    recommendedPICIds={recommendedPICIds ?? []}
                    bulkSelectablePICIds={bulkSelectablePICIds ?? []}
                    focusedCategoryId={focusedCategoryId}
                    recommendedPICVendorNames={recommendedPICVendorNames}
                    onChecked={handleChecked}
                    onBulkChecked={handleBulkChecked}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </StandardPageWrapper>
  );
}
